'use client';

import styles from './RecommendationPage.module.scss';
import { useAppSelector } from 'lib/hooks';
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { selectHasRecommendation } from 'slices/selectors';
import CaretIcon from 'components/icons/CaretIcon';
import { useCloseOnOutsideClick } from 'hooks/useCloseOnOutsideClick';

type SortDropdownProps = {
  updateSorting: (order: string) => void;
  active: string;
  type: 'ma' | 'mapd' | 'pdp' | 'gap';
};

const SortDropdown = ({ type, active, updateSorting }: SortDropdownProps) => {
  const hasRec = useAppSelector(selectHasRecommendation);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const openViaKeyboardRef = useRef(false);

  const options = [
    ...(hasRec
      ? [{ value: 'score', label: 'ALEX Plan Score (high to low)' }]
      : []),
    { value: 'premium', label: 'Premium (low to high)' },
    ...(type !== 'gap'
      ? [{ value: 'stars', label: 'Star Rating (high to low)' }]
      : []),
  ];

  const activeOption =
    options.find((opt) => opt.value === active) || options[0];

  useCloseOnOutsideClick(dropdownRef, isOpen, setIsOpen);

  // Focus first option when opened via keyboard (ArrowDown on trigger)
  useEffect(() => {
    if (isOpen && openViaKeyboardRef.current) {
      dropdownRef.current
        ?.querySelector<HTMLElement>('[role="option"]')
        ?.focus();
      openViaKeyboardRef.current = false;
    }
  }, [isOpen]);

  const handleTriggerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'ArrowDown' && !isOpen) {
        e.preventDefault();
        openViaKeyboardRef.current = true;
        setIsOpen(true);
      }
    },
    [isOpen]
  );

  const handleListboxKeyDown = useCallback(
    (e: KeyboardEvent<HTMLUListElement>) => {
      const items = Array.from(
        e.currentTarget.querySelectorAll<HTMLElement>('[role="option"]')
      );
      const currentIndex = items.indexOf(document.activeElement as HTMLElement);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          items[(currentIndex + 1) % items.length]?.focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          items[(currentIndex - 1 + items.length) % items.length]?.focus();
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          triggerRef.current?.focus();
          break;
        case 'Home':
          e.preventDefault();
          items[0]?.focus();
          break;
        case 'End':
          e.preventDefault();
          items[items.length - 1]?.focus();
          break;
      }
    },
    []
  );

  const handleOptionClick = useCallback(
    (value: string) => {
      updateSorting(value);
      setIsOpen(false);
    },
    [updateSorting]
  );

  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return (
    <div className={styles.sort} ref={dropdownRef}>
      <label htmlFor="sort-select">Sort By</label>
      <div className={styles.sortDropdown}>
        <button
          type="button"
          id="sort-select"
          ref={triggerRef}
          role="combobox"
          aria-controls="sort-listbox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          className={`${styles.sortButton} ${isOpen ? styles.open : ''}`}
          onClick={toggleDropdown}
          onKeyDown={handleTriggerKeyDown}
        >
          <span className={styles.sortButtonLabel}>{activeOption.label}</span>
          <span className={styles.sortButtonMobileLabel}>Sort by</span>
          <CaretIcon
            direction={isOpen ? 'up' : 'down'}
            size="large"
            color="gray"
          />
        </button>
        {isOpen && (
          <ul
            role="listbox"
            id="sort-listbox"
            className={styles.sortList}
            onKeyDown={handleListboxKeyDown}
          >
            {options.map((option) => {
              const isSelected = option.value === active;
              return (
                <li key={option.value} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleOptionClick(option.value)}
                    className={`${styles.sortOption} ${
                      isSelected ? styles.selected : ''
                    }`}
                  >
                    {option.label}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SortDropdown;
